import { useRef, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageHeroDecor from '@/components/feature/PageHeroDecor';
import { Link } from 'react-router-dom';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';
import { useSEO } from '@/hooks/useSEO';

const services = [
  {
    category: 'Strategy',
    categoryJp: '戦略設計',
    desc: '組織が社会に向けて発信する「軸」をつくります。意味の言語化から始め、ブランドの骨格を設計します。',
    items: [
      { title: '意味設計・ブランド戦略', body: '組織の存在意義を言語化し、社会に届く「軸」を設計します。ミッション・ビジョン・バリューの策定から、ブランドの核となるコンセプト開発まで。' },
      { title: 'ポジショニング設計', body: '競合との差異化ではなく、「この組織にしかできないこと」を明確にします。市場における独自の立ち位置を、意味から逆算して設計します。' },
      { title: 'ビジョン・ミッション策定', body: '組織の内側にある思いを掘り起こし、スタッフ全員が誇りを持って語れる言葉に変換します。外向けの発信と内向けの文化形成を同時に実現します。' },
    ],
  },
  {
    category: 'Content',
    categoryJp: 'コンテンツ設計',
    desc: '「伝わる言葉」を、あらゆる接点に実装します。物語の設計から媒体展開まで、一貫したディレクションを行います。',
    items: [
      { title: 'コンテンツ戦略・設計', body: '何を、誰に、どの順序で伝えるか。コンテンツの全体設計を行い、各媒体への展開方針を策定します。単発の制作ではなく、体系的な情報設計を提供します。' },
      { title: '編集・ライティング監修', body: '専門性を持ちながら読みやすく、共感を生む文章の設計と監修を行います。ウェブ、冊子、報告書など媒体を問わず対応します。' },
      { title: 'ストーリーテリング設計', body: '組織の歴史・実績・人物を「物語」として再構成します。事例紹介、スタッフインタビュー、活動報告など、読む人の心に残るコンテンツを設計します。' },
    ],
  },
  {
    category: 'Operation',
    categoryJp: '運用支援',
    desc: '一度つくって終わりではなく、循環する構造を育てます。発信の仕組み化から関係者の巻き込みまで、継続的に伴走します。',
    items: [
      { title: '発信体制の設計・構築', body: '外部に依存しない、組織内部で継続できる発信の仕組みをつくります。担当者の育成、ワークフローの設計、ツールの選定まで含めて支援します。' },
      { title: 'コミュニティ形成支援', body: '支持者・利用者・関係者が自ら語り、広めたくなる関係性を設計します。イベント設計、参加型コンテンツの企画、関係者との対話の場づくりを支援します。' },
      { title: '継続的な伴走支援', body: 'プロジェクト完了後も、定期的な対話と改善を続けます。月次レビュー、年次の意味の棚卸し、新たな課題への対応など、長期的なパートナーとして関わります。' },
    ],
  },
];

const flow = [
  { num: '01', title: 'ヒアリング', body: '組織の現状、課題、目指す姿を丁寧に伺います。答えを持ち込まず、対話の中から意味を掘り起こします。' },
  { num: '02', title: '意味の言語化', body: '組織の専門性・歴史・実践から、社会に届く「意味」を言語化します。コンセプトワードや物語の軸を設計します。' },
  { num: '03', title: '構造設計', body: '言語化された意味を、どのように社会に届けるかの構造を設計します。媒体・コンテンツ・関係者の役割を整理します。' },
  { num: '04', title: '実装・制作', body: '設計した構造に基づき、各種コンテンツや媒体を制作・実装します。外部パートナーとの連携も含めてディレクションします。' },
  { num: '05', title: '循環の育成', body: '発信が循環し始めたら、その構造を組織内部に定着させます。担当者の育成、仕組みの改善、継続的な伴走を行います。' },
];

function FadeIn({ children, delay = 0, className = '', from = 'bottom' }: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  from?: 'bottom' | 'left' | 'right';
}) {
  const { ref, isVisible } = useScrollAnimation();
  const translateMap = { bottom: 'translateY(28px)', left: 'translateX(-28px)', right: 'translateX(28px)' };
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translate(0)' : translateMap[from],
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

function StaggerList({ items, renderItem }: {
  items: typeof flow;
  renderItem: (item: typeof flow[0], idx: number) => React.ReactNode;
}) {
  const { ref, visibleCount } = useStaggerAnimation(items.length);
  return (
    <div ref={ref as React.RefObject<HTMLDivElement>} className="space-y-0 border-t border-divider">
      {items.map((item, idx) => (
        <div
          key={item.num}
          style={{
            opacity: visibleCount > idx ? 1 : 0,
            transform: visibleCount > idx ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.5s ease, transform 0.5s ease',
          }}
        >
          {renderItem(item, idx)}
        </div>
      ))}
    </div>
  );
}

export default function DekirukotoPage() {
  useSEO({
    title: 'できること・サービス内容 | メセナインターコム株式会社',
    description: 'メセナインターコム株式会社のサービス内容。戦略設計・コンテンツ設計・運用支援の3領域で、医療・福祉・教育・地域・文化の組織の意味を社会に届ける構造をつくります。ブランド戦略・ポジショニング設計・ストーリーテリングなど。',
    keywords: 'サービス,戦略設計,コンテンツ設計,運用支援,ブランド戦略,意味設計,医療,福祉,教育,コンサルティング',
    ogTitle: 'できること・サービス内容 | メセナインターコム株式会社',
    ogDescription: '戦略設計・コンテンツ設計・運用支援の3領域で、組織の意味を社会に届ける構造をつくります。',
    ogUrl: 'https://mecena-intercom.co.jp/dekirukoto',
    canonical: 'https://mecena-intercom.co.jp/dekirukoto',
  });

  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    });
  }, []);

  return (
    <div className="bg-warm-white min-h-screen">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-40 pb-28 md:pt-52 md:pb-40 bg-warm-white relative overflow-hidden">
          <PageHeroDecor variant="amber" />
          <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-12">
              <div
                ref={heroRef}
                className="md:col-span-8 md:col-start-3"
                style={{ opacity: 0, transform: 'translateY(32px)', transition: 'opacity 0.9s ease 0.1s, transform 0.9s ease 0.1s' }}
              >
                <div className="flex items-center gap-4 mb-10">
                  <span className="block w-6 h-px bg-accent-teal" />
                  <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Services</span>
                </div>
                <h1 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-4xl md:text-5xl mb-12">
                  できること
                </h1>
                <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp max-w-xl">
                  戦略設計・コンテンツ設計・運用支援の3領域で、<br />
                  組織の意味を社会に届ける構造をつくります。<br />
                  部分的な支援も、全体設計も、状況に合わせて対応します。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        {services.map((s, si) => (
          <section
            key={s.category}
            className={`relative overflow-hidden ${
              si === 0
                ? 'texture-dark py-28 md:py-44 bg-forest-green'
                : si % 2 === 0
                ? 'texture-philosophy py-20 md:py-32 bg-warm-light'
                : 'texture-process py-20 md:py-32 bg-warm-white'
            }`}
          >
            {/* セクション固有の装飾 */}
            {si === 0 && (
              <>
                {/* Strategy: 「戦略の核」— 放射状の構造 */}
                <div className="absolute top-0 right-0 w-32 h-32 lg:w-64 lg:h-64 pointer-events-none overflow-hidden">
                  <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(74,158,142,0.15) 1.5px, transparent 1.5px)', backgroundSize: '18px 18px' }} />
                </div>
                <div className="absolute -bottom-16 -left-16 w-[200px] h-[200px] lg:w-[400px] lg:h-[400px] rounded-full border border-accent-teal/12 pointer-events-none" />
                <div className="absolute top-8 right-8 lg:top-16 lg:right-20 w-8 h-8 lg:w-16 lg:h-16 border border-accent-teal/25 rotate-45 pointer-events-none" />
                <div className="absolute top-12 right-12 lg:top-24 lg:right-28 w-4 h-4 lg:w-8 lg:h-8 border border-accent-gold/30 rotate-45 pointer-events-none" />
              </>
            )}
            {si === 1 && (
              <>
                {/* Content: 「コンテンツ・物語」— 水平線の積み重なり */}
                <div className="absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none hidden lg:block">
                  {[0,1,2,3,4].map(i => (
                    <div key={i} className="mb-3" style={{ width: 20 + i * 10, height: 1, background: `rgba(212,122,46,${0.22 - i * 0.03})` }} />
                  ))}
                </div>
                <div className="absolute -top-10 -right-10 w-[160px] h-[160px] lg:w-[300px] lg:h-[300px] rounded-full border border-accent-amber/14 pointer-events-none" />
              </>
            )}
            {si === 2 && (
              <>
                {/* Operation: 「循環・継続」— 同心円 */}
                <div className="absolute -bottom-12 -right-12 w-[180px] h-[180px] lg:w-[360px] lg:h-[360px] rounded-full border border-accent-gold/14 pointer-events-none" />
                <div className="absolute top-0 left-0 w-24 h-24 lg:w-48 lg:h-48 pointer-events-none overflow-hidden">
                  <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(201,168,76,0.18) 1.5px, transparent 1.5px)', backgroundSize: '16px 16px' }} />
                </div>
              </>
            )}
            <div className="max-w-7xl mx-auto px-6 md:px-10">
              {si === 0 ? (
                /* ── Strategy: 特大・ダーク背景 ── */
                <>
                  <FadeIn delay={0} from="left">
                    <div className="mb-20">
                      <div className="flex items-center gap-4 mb-8">
                        <span className="block w-10 h-px bg-accent-teal" />
                        <span className="text-[11px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">{s.category}</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
                        <div className="md:col-span-6">
                          <h2 className="font-serif-jp font-light text-warm-white tracking-normal-jp leading-tight text-5xl md:text-7xl mb-0">
                            {s.categoryJp}
                          </h2>
                        </div>
                        <div className="md:col-span-5 md:col-start-8 pb-2">
                          <p className="font-sans-jp text-sm text-warm-white/60 leading-relaxed-jp">{s.desc}</p>
                        </div>
                      </div>
                    </div>
                  </FadeIn>
                  <ServiceGrid items={s.items} bg="bg-forest-green" dark />
                </>
              ) : (
                /* ── Content / Operation: 通常 ── */
                <>
                  <FadeIn delay={0} from="left">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
                      <div className="md:col-span-4">
                        <div className="flex items-center gap-4 mb-4">
                          <span className="block w-6 h-px bg-accent-teal" />
                          <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">{s.category}</span>
                        </div>
                        <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-3xl mb-6">{s.categoryJp}</h2>
                        <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">{s.desc}</p>
                      </div>
                    </div>
                  </FadeIn>
                  <ServiceGrid items={s.items} bg={si % 2 === 0 ? 'bg-warm-light' : 'bg-warm-white'} />
                </>
              )}
            </div>
          </section>
        ))}


        {/* Flow */}
        <section className="texture-process py-20 md:py-32 bg-warm-light relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <FadeIn delay={0}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
                <div className="md:col-span-5">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="block w-6 h-px bg-accent-teal" />
                    <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">How We Work</span>
                  </div>
                  <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-3xl md:text-4xl">
                    進め方の流れ
                  </h2>
                </div>
              </div>
            </FadeIn>
            <StaggerList
              items={flow}
              renderItem={(f) => (
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 py-10 border-b border-divider items-start">
                  <div className="md:col-span-1">
                    <span className="font-garamond text-3xl font-light text-divider">{f.num}</span>
                  </div>
                  <div className="md:col-span-3">
                    <h3 className="font-serif-jp font-light text-charcoal tracking-normal-jp text-xl">{f.title}</h3>
                  </div>
                  <div className="md:col-span-8">
                    <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">{f.body}</p>
                  </div>
                </div>
              )}
            />
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 md:py-28 bg-warm-white border-t border-divider">
          <FadeIn delay={0}>
            <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
              <div>
                <h3 className="font-serif-jp font-light text-charcoal tracking-normal-jp text-2xl mb-3">
                  まずは、現状を一緒に整理しましょう。
                </h3>
                <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">
                  課題が明確でなくても構いません。30分の対話から始められます。
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
                <Link to="/cases" className="inline-flex items-center gap-2 px-7 py-3.5 border border-charcoal text-sm font-sans-jp text-charcoal tracking-wide-jp hover:bg-charcoal hover:text-warm-white transition-all duration-300 whitespace-nowrap">
                  事例を見る →
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3.5 bg-charcoal text-warm-white text-sm font-sans-jp tracking-wide-jp hover:bg-charcoal-soft transition-colors duration-300 whitespace-nowrap">
                  相談してみる
                </Link>
              </div>
            </div>
          </FadeIn>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function ServiceGrid({ items, bg, dark = false }: { items: typeof services[0]['items']; bg: string; dark?: boolean }) {
  const { ref, visibleCount } = useStaggerAnimation(items.length);
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`grid grid-cols-1 md:grid-cols-3 gap-px ${dark ? 'bg-warm-white/10 border border-warm-white/10' : 'bg-divider border border-divider'}`}
    >
      {items.map((item, idx) => (
        <div
          key={item.title}
          className={`${bg} ${dark ? 'p-12 md:p-16' : 'p-10 md:p-12'} flex flex-col gap-6`}
          style={{
            opacity: visibleCount > idx ? 1 : 0,
            transform: visibleCount > idx ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.5s ease, transform 0.5s ease',
          }}
        >
          <h3 className={`font-serif-jp font-light tracking-normal-jp leading-snug ${dark ? 'text-warm-white text-xl' : 'text-charcoal text-lg'}`}>
            {item.title}
          </h3>
          <p className={`font-sans-jp text-sm leading-relaxed-jp ${dark ? 'text-warm-white/55' : 'text-muted-brown'}`}>{item.body}</p>
        </div>
      ))}
    </div>
  );
}
